import React, { useState, useEffect } from "react";
import { getAndroidUser } from "../utils/androidBridge";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setMobileNumber, setConsentHandle, setDeviceId } from "../features/auth/authSlice";
import { triggerOtpThunk } from "../features/auth/authThunk";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";

const Login: React.FC = () => {
    const [mobile, setMobile] = useState("");
    const [isAutoTriggering, setIsAutoTriggering] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { loading, error, deviceId, consentHandle: stateConsentHandle, mobileNumber: stateMobileNumber } = useAppSelector((state) => state.auth);

    // Get client name and dynamic consentHandle from URL if provided
    const searchParams = new URLSearchParams(location.search);
    const clientName = searchParams.get("client") || "CAMSfinserv";
    const urlConsentHandle = searchParams.get("consentHandle");
    const urlMobile = searchParams.get("mobile") || searchParams.get("mobileNumber") || stateMobileNumber;
    const urlDeviceId = searchParams.get("deviceId");

    const androidDeviceIdRef = React.useRef("");
    const hasAutoTriggered = React.useRef(false);

    const executeSendOtp = async (targetMobile: string) => {
        if (targetMobile.length === 10) {
            dispatch(setMobileNumber(targetMobile));

            // Generate a unique UUID type of consent handle each time OTP is sent
            const generateUuid = () => {
                return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                    const r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
                    return v.toString(16);
                });
            };
            const sessionUuid = generateUuid();
            const freshConsentHandle = sessionUuid;

            const finalDeviceId = urlDeviceId || androidDeviceIdRef.current || deviceId || sessionUuid;

            // Persist locally in Redux explicitly
            dispatch(setDeviceId(finalDeviceId));
            dispatch(setConsentHandle(freshConsentHandle));

            // Trigger OTP with the newly generated handle
            const resultAction = await dispatch(
                triggerOtpThunk({
                    mobileNumber: targetMobile,
                    deviceId: finalDeviceId,
                    consentHandle: freshConsentHandle
                })
            );
            if (triggerOtpThunk.fulfilled.match(resultAction)) {
                navigate("/verifyOtp");
            } else {
                setIsAutoTriggering(false);
                hasAutoTriggered.current = false;
            }
        }
    };

    useEffect(() => {
        if (hasAutoTriggered.current) return;

        const { mobileNumber, deviceId: androidId } = getAndroidUser();
        if (mobileNumber) setMobile(mobileNumber);
        if (androidId) androidDeviceIdRef.current = androidId;

        // Only set handle if it comes from the URL
        if (urlConsentHandle) {
            dispatch(setConsentHandle(urlConsentHandle));
        }

        // Auto-trigger if mobile is in URL OR if mobile number comes from bridge
        const autoMobile = urlMobile || mobileNumber;
        if (autoMobile && autoMobile.length === 10) {
            hasAutoTriggered.current = true;
            setMobile(autoMobile);
            setIsAutoTriggering(true);
            
            // Allow the UI to update first to "Verifying details..." before making API request
            setTimeout(() => {
                executeSendOtp(autoMobile);
            }, 100);
        }
    }, [dispatch, urlConsentHandle, urlMobile, stateMobileNumber]);

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        await executeSendOtp(mobile);
    };

    if (isAutoTriggering && !error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-fin-bg text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fin-primary mb-4"></div>
                <h2 className="text-xl font-semibold text-white">Verifying details...</h2>
                <p className="text-fin-slate mt-2">Sending OTP to {mobile || urlMobile}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-fin-bg">
            <div className="w-full max-w-sm">
                <div className="text-center mb-6">
                    {/* Placeholder for Our Logo */}
                    <div className="mx-auto h-16 w-16 mb-4 flex items-center justify-center bg-fin-surface rounded-full border border-fin-border">
                        {/* Replace with actual company logo img tag */}
                        <svg className="w-8 h-8 text-fin-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" /></svg>
                    </div>
                    <p className="text-[15px] font-medium text-fin-slate">
                        You have been securely redirected from
                    </p>
                    <h1 className="text-2xl font-bold text-white mt-1">{clientName}</h1>
                </div>

                {/* Security Card */}
                <Card className="mb-6 bg-fin-surface border-fin-border shadow-none flex items-start p-4">
                    <div className="h-10 w-10 rounded-full bg-fin-primary/10 flex items-center justify-center flex-shrink-0 mr-4">
                        <svg className="h-5 w-5 text-fin-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-sm">Your data is safe and secure</h3>
                        <p className="text-xs text-fin-slate mt-1 leading-relaxed">
                            We use bank-grade encryption to ensure your information is protected at all times.
                        </p>
                    </div>
                </Card>

                <Card className="mb-6">
                    <form onSubmit={handleSendOtp} className="space-y-6">
                        <div className="flex flex-col space-y-2 mb-4">
                            <label className="text-sm font-semibold text-fin-slate">Mobile Number</label>
                            <Input
                                type="tel"
                                maxLength={10}
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                                placeholder="Enter mobile number"
                                required
                                leftIcon={<span className="text-white font-medium">+91</span>}
                            />
                        </div>

                        {error && (
                            <div className="rounded-md bg-red-900/20 p-4 border border-red-900/40">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-red-400">Connection Error</h3>
                                        <div className="mt-1 text-sm text-red-300">{error}</div>
                                    </div>
                                </div>
                                {error?.includes("Consent handle is missing") && (
                                    <div className="mt-3 text-xs text-red-400 bg-red-900/10 p-2 rounded border border-red-900/20 italic">
                                        Please ensure you have arrived here through a valid partner link.
                                    </div>
                                )}
                            </div>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            isLoading={loading}
                            disabled={mobile.length !== 10}
                        >
                            Send OTP
                        </Button>
                    </form>
                </Card>

                <p className="text-center text-xs text-fin-slate">
                    By continuing, you agree to our <a href="/terms" className="text-fin-primary hover:text-fin-400">Terms of Service</a> and <a href="/privacy" className="text-fin-primary hover:text-fin-400">Privacy Policy</a>.
                </p>
            </div>
        </div>
    );
};

export default Login;
